import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const CONSENT_KEY = "nomirio-cookie-consent";
const CONSENT_VERSION = 1;
export const COOKIE_SETTINGS_EVENT = "nomirio:open-cookie-settings";

export interface CookieConsentValue {
  version: number;
  timestamp: string;
  necessary: true;
  functional: boolean;
  analytics: boolean;
}

export const getCookieConsent = (): CookieConsentValue | null => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsentValue;
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveConsent = (functional: boolean, analytics: boolean) => {
  const value: CookieConsentValue = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    necessary: true,
    functional,
    analytics,
  };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
  } catch {
    // storage unavailable — fail silently
  }
};

export const openCookieSettings = () => {
  window.dispatchEvent(new CustomEvent(COOKIE_SETTINGS_EVENT));
};

const categories = [
  {
    key: "necessary" as const,
    title: "Strictly Necessary",
    locked: true,
    description:
      "Required for login, sessions, payments and security. These cannot be disabled.",
  },
  {
    key: "functional" as const,
    title: "Functional",
    locked: false,
    description:
      "Remember your preferences like theme and dismissed notices for a consistent experience.",
  },
  {
    key: "analytics" as const,
    title: "Analytics",
    locked: false,
    description:
      "Help us understand how the marketplace is used so we can improve it. Aggregated and anonymous.",
  },
];

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [functional, setFunctional] = useState(true);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) {
      const id = window.setTimeout(() => setShowBanner(true), 800);
      return () => window.clearTimeout(id);
    }
  }, []);

  useEffect(() => {
    const open = () => {
      const existing = getCookieConsent();
      setFunctional(existing?.functional ?? true);
      setAnalytics(existing?.analytics ?? true);
      setShowPrefs(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, open);
  }, []);

  const finish = (fn: boolean, an: boolean) => {
    saveConsent(fn, an);
    setShowBanner(false);
    setShowPrefs(false);
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && !showPrefs && (
          <motion.div
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-4"
            role="dialog"
            aria-label="Cookie consent"
          >
            <div className="container mx-auto max-w-5xl glass rounded-xl border border-border shadow-lg p-4 sm:p-5">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <Cookie className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">We value your privacy</p>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      We use cookies to keep you signed in, secure transactions, and understand how
                      Nomirio is used. You can accept all, decline optional ones, or customize.{" "}
                      <Link to="/cookie-policy" className="text-primary hover:underline">
                        Cookie Policy
                      </Link>
                    </p>
                  </div>
                </div>
                <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setFunctional(true);
                      setAnalytics(true);
                      setShowPrefs(true);
                    }}
                  >
                    Manage Preferences
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => finish(false, false)}>
                    Decline
                  </Button>
                  <Button size="sm" onClick={() => finish(true, true)}>
                    Accept All
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={showPrefs} onOpenChange={setShowPrefs}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="h-5 w-5 text-primary" />
              Cookie Preferences
            </DialogTitle>
            <DialogDescription>
              Choose which cookies you allow. Strictly necessary cookies are always active. Read
              more in our{" "}
              <Link to="/cookie-policy" className="text-primary hover:underline" onClick={() => setShowPrefs(false)}>
                Cookie Policy
              </Link>
              .
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {categories.map((cat) => {
              const checked =
                cat.key === "necessary" ? true : cat.key === "functional" ? functional : analytics;
              return (
                <div
                  key={cat.key}
                  className="flex items-start justify-between gap-4 rounded-lg border border-border surface-2 p-3"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {cat.title}
                      {cat.locked && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-primary">
                          Always on
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{cat.description}</p>
                  </div>
                  <Switch
                    checked={checked}
                    disabled={cat.locked}
                    onCheckedChange={(v) => {
                      if (cat.key === "functional") setFunctional(v);
                      if (cat.key === "analytics") setAnalytics(v);
                    }}
                    aria-label={`Toggle ${cat.title} cookies`}
                  />
                </div>
              );
            })}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" size="sm" onClick={() => finish(false, false)}>
              Decline All
            </Button>
            <Button variant="secondary" size="sm" onClick={() => finish(functional, analytics)}>
              Save Preferences
            </Button>
            <Button size="sm" onClick={() => finish(true, true)}>
              Accept All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;